import * as ACTIONS from 'constant/action/dataset'
import * as LOADER_ACTIONS from 'constant/action/loader'
import * as PAGINATION from 'constant/pagination'
import * as TABLE from 'constant/table'
import * as NUMBER from 'constant/number'
import buildFileMeta from 'helper/build-file-meta'
import buildImageFile from 'helper/build-image-file'
import { createAction } from 'redux-actions'
import isDraft from 'helper/is-draft'
import isPublished from 'helper/is-published'
import buildDescriptionIframes from 'helper/build-description-iframes'
import _ from 'lodash'

const updateDatasets = createAction(ACTIONS.UPDATE_DATASETS)
const updateDatasetSubmitting = createAction(LOADER_ACTIONS.UPDATE_DATASET_SUBMITTING)
const updateLoading = createAction(LOADER_ACTIONS.UPDATE_LOADING)
const updatePagination = createAction(ACTIONS.UPDATE_DATASET_PAGINATION)
const updateDatasetSearchTerm = createAction(ACTIONS.UPDATE_DATASET_SEARCH_TERM)

export const updateDatasetDescription = createAction(ACTIONS.UPDATE_DATASET_DESCRIPTION)

export const getDatasets = (sort = PAGINATION.SORT.DATASETS, direction = PAGINATION.DESCENDING, start = NUMBER.ZERO, pagination = true) => async (dispatch, getState, { dataset }) => {
  dispatch(updateLoading(true))
  dispatch(updatePagination({
    direction,
    sort,
    start
  }))

  const {
    dataset: datasetState
  } = getState()

  const datasets = await dataset.getDatasets(sort, direction, TABLE.PAGE_SIZE, _.isNumber(start) ? start : _.get(datasetState, 'dataset.start'), pagination)
  dispatch(updateDatasets(datasets))
  dispatch(updateLoading(false))
}

export const searchDatasets = (searchTerm, sort = PAGINATION.SORT.ARTICLES, direction = PAGINATION.DESCENDING, start = NUMBER.ZERO) => async (dispatch, getState, { dataset }) => {
  dispatch(updateDatasetSearchTerm(searchTerm))
  if (!searchTerm) {
    return dispatch(getDatasets())
  }

  dispatch(updateLoading(true))
  dispatch(updatePagination({
    direction,
    sort,
    start
  }))
  const {
    dataset: datasetState
  } = getState()
  const response = await dataset.searchDatasets(searchTerm, sort, direction, TABLE.PAGE_SIZE, _.isNumber(start) ? start : _.get(datasetState, 'dataset.start'))
  dispatch(updateDatasets(response))
  dispatch(updateLoading(false))
}

export const addNewDataset = (datasetObject) => async (dispatch, getState, { dataset }) => {
  try {
    dispatch(updateDatasetSubmitting(true))

    datasetObject.description_meta = buildDescriptionIframes(datasetObject.description_meta)
    datasetObject.image_meta = buildImageFile(datasetObject.image_meta)

    const {
      file: imageFile,
      meta: imageMeta
    } = buildFileMeta(datasetObject.image_meta, datasetObject.title)
    const { file: descriptionFile, meta: descriptionMeta } = buildFileMeta(datasetObject.description_meta, datasetObject.title)
    const insightIsDraft = isDraft(datasetObject)
    const newDataset = {
      ...datasetObject,
      article_type_uuid: 'b73b2d11-60b2-4ac7-934e-8f68093e7746',
      description_meta: descriptionMeta,
      image_meta: imageMeta,
      is_draft: insightIsDraft,
      published: isPublished(insightIsDraft, datasetObject.published),
      send_emails: datasetObject.send_emails === 'true'
    }
    const response = await dataset.addNewDataset(newDataset)

    if (imageFile) {
      await dataset.uploadFile(response.image_url, imageFile)
    }

    if (descriptionFile) {
      await dataset.uploadFile(response.description_url, descriptionFile)
    }

    dispatchNewDataset(dispatch, getState)

    return newDataset
  } catch (error) {
    console.error(error)
    return { error: true, message: error.message }
  }
}

const dispatchNewDataset = (dispatch, getState) => {
  const { dataset: { pagination: { sort, direction, start } } } = getState()

  dispatch(updateDatasetSubmitting(false))
  dispatch(getDatasets(sort, direction, start))
}

export const editDataset = (datasetObject) => async (dispatch, getState, { dataset }) => {
  try {
    dispatch(updateDatasetSubmitting(true))
    const { dataset: { datasetDescription, pagination: { sort, direction, start } } } = getState()
    datasetObject.description_meta = buildDescriptionIframes(datasetObject.description_meta, datasetDescription)
    datasetObject.image_meta = buildImageFile(datasetObject.image_meta)
    const insightIsDraft = isDraft(datasetObject)
    const {
      file: imageFile,
      meta: imageMeta
    } = buildFileMeta(datasetObject.image_meta, datasetObject.title)

    const {
      file: descriptionFile,
      meta: descriptionMeta
    } = buildFileMeta(datasetObject.description_meta, datasetObject.title)
    const editedDataset = {
      ...datasetObject,
      description_meta: descriptionMeta,
      image_meta: imageMeta,
      is_draft: insightIsDraft,
      published: isPublished(insightIsDraft, datasetObject.published),
      send_emails: datasetObject.send_emails === 'true'
    }

    const response = await dataset.editDataset(editedDataset)

    if (imageFile) {
      await dataset.uploadFile(response.image_url, imageFile)
    }

    if (descriptionFile) {
      await dataset.uploadFile(response.description_url, descriptionFile)
    }

    dispatch(updateDatasetSubmitting(false))
    dispatch(getDatasets(sort, direction, start))
    return editedDataset
  } catch (error) {
    console.error(error)
    return { error: true, message: error.message }
  }
}

export const deleteDataset = (uuid) => async (dispatch, getState, { article }) => {
  await article.deleteArticle(uuid, 'dataset')
  const {
    dataset: {
      pagination: {
        sort,
        direction,
        start
      }
    }
  } = getState()
  dispatch(getDatasets(sort, direction, start))
}

export const publishDataset = (uuid) => async (dispatch, getState, { article }) => {
  await article.publishArticle(uuid)

  const {
    dataset: {
      pagination: {
        sort,
        direction,
        start
      }
    }
  } = getState()

  dispatch(getDatasets(sort, direction, start))
}

export const pinDataset = (uuid) => async (dispatch, getState, { article }) => {
  await article.pinArticle(uuid)

  const {
    dataset: {
      pagination: {
        sort,
        direction,
        start
      }
    }
  } = getState()

  dispatch(getDatasets(sort, direction, start))
}

export const downloadDatasets = () => async (dispatch, getState, { dataset }) => {
  try {
    const {
      table: {
        columns: {
          DATASET: columns
        }
      }
    } = getState()
    const { objUrl } = await dataset.downloadDatasets(columns)
    window.open(objUrl)
  } catch (error) {
    console.error(error)
  }
}
